import { hasConfiguredMargeOperand } from "./margeFormula";

const COMPONENT_LABELS = {
    foreignshop_stock: "Fremdshop-Verfügbarkeit",
    maxPriceAdjustment: "Mindest-/Maximalpreis",
    priceAdjustments: "Preisangleichung",
    own_stock: "Eigener Bestand",
    product_filter: "Produktfilter",
    categories: "Kategorien",
    shops: "Fremdshops",
    productAttributes: "Produktattribute",
    dateComponent: "Datum/Zeit",
    priceRounding: "Preisrundung",
    calculate_by_channel: "Kalkulation auf Kanal",
    adjustmentByMarge: "Preisanpassung nach Marge",
    useAllPricesFromGroup: "Alle Preise aus Gruppe",
    checkboxes: "Optionen",
    channel: "Kanal",
};

const CHANGE_TYPE_META = {
    "changed component": { label: "Komponente geändert", variant: "primary" },
    "added component": { label: "Komponente hinzugefügt", variant: "success" },
    "deleted component": { label: "Komponente entfernt", variant: "danger" },
    "changed name": { label: "Name geändert", variant: "secondary" },
    "changed description": { label: "Beschreibung geändert", variant: "secondary" },
    "changed group": { label: "Gruppe geändert", variant: "secondary" },
    "changed priority": { label: "Priorität geändert", variant: "secondary" },
    "changed exception rule flag": { label: "Ausnahmeregel geändert", variant: "secondary" },
    "changed backup rule": { label: "Backup-Regel geändert", variant: "info" },
    "created rule": { label: "Regel erstellt", variant: "success" },
    "deleted rule": { label: "Regel gelöscht", variant: "danger" },
    "activated rule": { label: "Regel aktiviert", variant: "success" },
    "deactivated rule": { label: "Regel deaktiviert", variant: "warning" },
    "changed enabled": { label: "Aktivierungsstatus geändert", variant: "info" },
};

const FIELD_LABELS = {
    stock: "Bestand",
    border_style: "Darstellung",
    foreignshop_stock: "Verknüpfung",
    maxPriceDecreaseValue: "Max. Preisminderung",
    maxPriceIncreaseValue: "Max. Preissteigerung",
    minimumEuroOverEK: "Mindestpreis über EK (€)",
    minimumPercent: "Mindestpreis (%)",
    minimumPriceFormulaBase: "Mindestpreis-Basis",
    maximumPriceFormulaBase: "Maximalpreis-Basis",
    minimumPriceFormulaAddTax: "MwSt. nach Mindestformel",
    maximumPriceFormulaAddTax: "MwSt. nach Maximalformel",
    minimumPrice: "Mindestpreis-Formel",
    maximumPrice: "Maximalpreis-Formel",
    isCoupon: "Als Gutschein",
    adjustmentType: "Vergleichspreis",
    adjustmentValues: "Formelwerte",
    isEuro: "In Euro",
    useAllProviders: "Alle Anbieter",
    undercutTotalPrice: "Gesamtpreis unterbieten",
    roundingType: "Rundungsart",
    roundingValues: "Rundungswerte",
    activateContraryBackupRuleRoundMode: "Gegenoperation bei Backup-Regel",
    lang: "Sprache",
    type: "Typ",
    value: "Wert",
    X: "Wert X",
    Y: "Wert Y",
    Z: "Wert Z",
    OP_1: "Operator 1",
    OP_2: "Operator 2",
    OP_3: "Operator 3",
    useFactor: "Feed-Faktor",
};

const ADJUSTMENT_TYPE_LABELS = {
    CHEAPEST_FS_PREIS: "günstigster Preis",
    SECOND_CHEAPEST_PRICE: "zweitgünstigster Preis",
    THIRD_CHEAPEST_PRICE: "drittgünstigster Preis",
    FOURTH_CHEAPEST_PRICE: "viertgünstigster Preis",
    FIFTH_CHEAPEST_PRICE: "fünftgünstigster Preis",
    MOST_EXPENSIVE: "teuerster Preis",
    AVERAGE_PRICE: "Durchschnittspreis",
    MEDIAN_PRICE: "Median",
    NEXT_PRICE_ABOVE_MIN_PRICE: "nächster Preis oberhalb des Mindestpreis (Exkl.)",
    NEXT_PRICE_ABOVE_MIN_PRICE_INCLUSIVE_UNDERCUTTING_VALUE: "nächster Preis oberhalb des Mindestpreis (Inkl.)",
};

const HISTORY_TYPE_LABELS = {
    rule_change: "Regeländerung",
    rule: "Regel",
};

const OPERATOR_LABELS = {
    PLUS: "+",
    MINUS: "-",
    MULTIPLICATION: "×",
    DIVISION: "÷",
};

function splitTopLevel(content) {
    const parts = [];
    let depth = 0;
    let current = "";

    for (let i = 0; i < content.length; i += 1) {
        const char = content[i];
        if (char === "{" || char === "[") {
            depth += 1;
        } else if (char === "}" || char === "]") {
            depth -= 1;
        } else if (char === "," && depth === 0) {
            if (current.trim()) {
                parts.push(current.trim());
            }
            current = "";
            continue;
        }
        current += char;
    }

    if (current.trim()) {
        parts.push(current.trim());
    }

    return parts;
}

function parseJavaLikeValue(rawValue) {
    const value = String(rawValue ?? "").trim();
    if (!value || value === "null") {
        return null;
    }
    if (value.startsWith("[")) {
        const inner = value.slice(1, -1).trim();
        if (!inner) {
            return [];
        }
        return splitTopLevel(inner).map((entry) => parseJavaLikeValue(entry));
    }
    if (value.startsWith("{")) {
        const inner = value.slice(1, -1).trim();
        const result = {};
        if (!inner) {
            return result;
        }
        splitTopLevel(inner).forEach((entry) => {
            const separatorIndex = entry.indexOf("=");
            if (separatorIndex === -1) {
                return;
            }
            const key = entry.slice(0, separatorIndex).trim();
            const entryValue = entry.slice(separatorIndex + 1).trim();
            result[key] = parseJavaLikeValue(entryValue);
        });
        return result;
    }
    if (value === "true") return true;
    if (value === "false") return false;
    if (value === "NaN") return null;
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && /^-?\d/.test(value)) {
        return numericValue;
    }
    return value;
}

function isNoiseValue(value) {
    return value === null
        || value === undefined
        || value === ""
        || value === "NaN"
        || (typeof value === "number" && Number.isNaN(value));
}

function formatOperator(value) {
    return OPERATOR_LABELS[value] || value;
}

function formatPrimitive(value, key = "") {
    if (isNoiseValue(value)) {
        return null;
    }
    if (key === "adjustmentType" && ADJUSTMENT_TYPE_LABELS[value]) {
        return ADJUSTMENT_TYPE_LABELS[value];
    }
    if (typeof value === "boolean") {
        return value ? "Ja" : "Nein";
    }
    if (key.startsWith("OP_")) {
        return formatOperator(value);
    }
    if (typeof value === "number") {
        if (value === -1 && (key.includes("Price") || key.includes("Value") || key.includes("Percent"))) {
            return "deaktiviert";
        }
        return String(value).replace(".", ",");
    }
    return String(value);
}

function formatFormula(formula, base) {
    if (!formula || typeof formula !== "object" || !hasConfiguredMargeOperand(formula)) {
        return null;
    }

    const baseLabel = base || "Einkaufspreis";
    let text = String(baseLabel);

    if (formula.OP_1 && !isNoiseValue(formula.X)) {
        text += ` ${formatOperator(formula.OP_1)} ${formatPrimitive(formula.X)}`;
    }
    if (formula.OP_2 && !isNoiseValue(formula.Y)) {
        text += ` ${formatOperator(formula.OP_2)} ${formatPrimitive(formula.Y)}%`;
    }
    if (formula.OP_3 && !isNoiseValue(formula.Z)) {
        text += ` ${formatOperator(formula.OP_3)} ${formatPrimitive(formula.Z)}`;
    }

    return text;
}

function formatAdjustmentValues(values) {
    if (!values || typeof values !== "object" || Array.isArray(values)) {
        return formatDisplayValue(values);
    }

    return Object.entries(values)
        .map(([threshold, entry]) => {
            if (!entry || typeof entry !== "object") {
                return `ab ${threshold}: ${formatDisplayValue(entry)}`;
            }
            const amount = formatPrimitive(entry.value, "value");
            const unit = entry.type || "€";
            if (!amount) {
                return null;
            }
            return `ab ${String(threshold).replace(".", ",")}: ${amount} ${unit}`;
        })
        .filter(Boolean)
        .join(", ");
}

function formatDisplayValue(value, key = "") {
    if (key === "minimumPrice" || key === "maximumPrice") {
        return null;
    }
    if (key === "adjustmentValues") {
        return formatAdjustmentValues(value);
    }
    if (isNoiseValue(value)) {
        return null;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return "—";
        }
        if (value.every((entry) => entry && typeof entry === "object" && !Array.isArray(entry))) {
            return value
                .map((entry, index) => {
                    const lines = formatObjectLines(entry);
                    if (!lines.length) {
                        return null;
                    }
                    return value.length > 1 ? `Eintrag ${index + 1}: ${lines.join("; ")}` : lines.join("; ");
                })
                .filter(Boolean)
                .join(" | ");
        }
        return value.map((entry) => formatPrimitive(entry)).filter(Boolean).join(", ");
    }
    if (value && typeof value === "object") {
        const lines = formatObjectLines(value);
        return lines.length ? lines.join("; ") : null;
    }
    return formatPrimitive(value, key);
}

function formatObjectLines(object, parentKey = "") {
    if (!object || typeof object !== "object") {
        return [];
    }

    const lines = [];

    if (parentKey === "maxPriceAdjustment" || (object.minimumPrice && object.maximumPrice)) {
        const minFormula = formatFormula(object.minimumPrice, object.minimumPriceFormulaBase);
        const maxFormula = formatFormula(object.maximumPrice, object.maximumPriceFormulaBase);
        if (minFormula) {
            lines.push(`Mindestpreis: ${minFormula}`);
        }
        if (maxFormula) {
            lines.push(`Maximalpreis: ${maxFormula}`);
        }
    }

    Object.entries(object).forEach(([key, value]) => {
        if (key === "minimumPrice" || key === "maximumPrice") {
            return;
        }
        const label = FIELD_LABELS[key] || key;
        const formatted = formatDisplayValue(value, key);
        if (!formatted) {
            return;
        }
        lines.push(`${label}: ${formatted}`);
    });

    return lines;
}

function flattenDiffLines(value, componentKey = "", prefix = "") {
    if (value === null || value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return prefix ? [{ path: prefix, value: "—" }] : [];
        }
        if (value.every((entry) => entry && typeof entry === "object" && !Array.isArray(entry))) {
            return value.flatMap((entry, index) => {
                const entryPrefix = value.length > 1 ? `${prefix}Eintrag ${index + 1}` : prefix;
                return flattenDiffLines(entry, componentKey, entryPrefix ? `${entryPrefix} · ` : "");
            });
        }
        const formatted = value.map((entry) => formatPrimitive(entry)).filter(Boolean).join(", ");
        return formatted ? [{ path: prefix || "Wert", value: formatted }] : [];
    }
    if (typeof value === "object") {
        if (!Array.isArray(value) && Object.keys(value).length === 0) {
            return prefix ? [{ path: prefix.replace(/ · $/, ""), value: "gesetzt" }] : [];
        }
        if (componentKey === "maxPriceAdjustment" || (value.minimumPrice && value.maximumPrice)) {
            const lines = [];
            const minFormula = formatFormula(value.minimumPrice, value.minimumPriceFormulaBase);
            const maxFormula = formatFormula(value.maximumPrice, value.maximumPriceFormulaBase);
            if (minFormula) {
                lines.push({ path: `${prefix}Mindestpreis`, value: minFormula });
            }
            if (maxFormula) {
                lines.push({ path: `${prefix}Maximalpreis`, value: maxFormula });
            }
            Object.entries(value).forEach(([key, entryValue]) => {
                if (key === "minimumPrice" || key === "maximumPrice") {
                    return;
                }
                lines.push(...flattenDiffLines(entryValue, componentKey, `${prefix}${FIELD_LABELS[key] || key}`));
            });
            return lines.filter((line) => line.value);
        }

        return Object.entries(value).flatMap(([key, entryValue]) =>
            flattenDiffLines(entryValue, componentKey, `${prefix}${FIELD_LABELS[key] || key}`)
        );
    }

    const formatted = formatDisplayValue(value);
    return formatted ? [{ path: prefix || "Wert", value: formatted }] : [];
}

function normalizeArrayContainer(value) {
    if (Array.isArray(value) && value.length === 1 && value[0] && typeof value[0] === "object") {
        return value[0];
    }
    return value;
}

function normalizeFormulaText(value) {
    return String(value || "")
        .replace(/\s\+\s0%/g, "")
        .replace(/\s\+\s0$/g, "")
        .trim();
}

function isCosmeticDiff(before, after) {
    if (before === after) {
        return true;
    }
    if (before === "—" && (after === "0" || after === "deaktiviert")) {
        return true;
    }
    return normalizeFormulaText(before) === normalizeFormulaText(after);
}

function buildFieldDiffs(beforeValue, afterValue, componentKey) {
    const beforeLines = flattenDiffLines(normalizeArrayContainer(beforeValue), componentKey);
    const afterLines = flattenDiffLines(normalizeArrayContainer(afterValue), componentKey);
    const paths = [...new Set([...beforeLines.map((line) => line.path), ...afterLines.map((line) => line.path)])];

    return paths
        .map((path) => {
            const before = beforeLines.find((line) => line.path === path)?.value ?? "—";
            const after = afterLines.find((line) => line.path === path)?.value ?? "—";
            if (isCosmeticDiff(before, after)) {
                return null;
            }
            return { path, before, after };
        })
        .filter(Boolean);
}

function parseChangeLine(line) {
    const match = line.match(/<strong>([^<]+)<\/strong>\s*(.*)$/i);
    if (!match) {
        return null;
    }

    const typeKey = match[1].trim().replace(/:+$/, "").toLowerCase();
    const rest = match[2].trim().replace(/\n/g, " ");
    const meta = CHANGE_TYPE_META[typeKey] || { label: match[1].trim().replace(/:+$/, ""), variant: "secondary" };

    if (typeKey === "changed component") {
        const componentMatch = rest.match(/^(\S+)\s*:\s*(.+)\s+->\s+(.+)$/);
        if (!componentMatch) {
            return { typeKey, ...meta, text: rest };
        }

        const componentKey = componentMatch[1];
        const beforeRaw = componentMatch[2].trim();
        const afterRaw = componentMatch[3].trim();
        const beforeValue = parseJavaLikeValue(beforeRaw);
        const afterValue = parseJavaLikeValue(afterRaw);
        const fieldDiffs = buildFieldDiffs(beforeValue, afterValue, componentKey);

        return {
            typeKey,
            ...meta,
            componentKey,
            componentLabel: COMPONENT_LABELS[componentKey] || componentKey,
            beforeValue,
            afterValue,
            fieldDiffs,
            hasStructuredDiff: fieldDiffs.length > 0,
        };
    }

    if (typeKey === "added component" || typeKey === "deleted component") {
        const componentMatch = rest.match(/^(\S+)\s*:\s*(.+)$/);
        if (!componentMatch) {
            return { typeKey, ...meta, text: rest };
        }

        const componentKey = componentMatch[1];
        const value = parseJavaLikeValue(componentMatch[2].trim());

        return {
            typeKey,
            ...meta,
            componentKey,
            componentLabel: COMPONENT_LABELS[componentKey] || componentKey,
            value,
            lines: formatObjectLines(
                Array.isArray(value) && value.length === 1 ? value[0] : value,
                componentKey
            ),
        };
    }

    if (rest.includes("->")) {
        const arrowIndex = rest.indexOf("->");
        const before = rest.slice(0, arrowIndex).trim();
        const after = rest.slice(arrowIndex + 2).trim();
        const formatEnabledState = (value) => {
            if (typeKey === "changed enabled") {
                if (value === "true") return "Aktiviert";
                if (value === "false") return "Deaktiviert";
            }
            return value || "—";
        };
        return {
            typeKey,
            ...meta,
            before: formatEnabledState(before),
            after: formatEnabledState(after),
        };
    }

    if (typeKey === "activated rule" || typeKey === "deactivated rule" || typeKey === "created rule") {
        return {
            typeKey,
            ...meta,
            text: rest || meta.label,
        };
    }

    return {
        typeKey,
        ...meta,
        text: rest,
    };
}

export function formatHistoryObjectLines(object, parentKey = "") {
    return formatObjectLines(object, parentKey);
}

export function stripHistoryMessageHtml(message) {
    if (!message) {
        return "";
    }
    return message
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<strong>([^<]+)<\/strong>/gi, "$1 ")
        .replace(/<[^>]+>/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

export function parseRuleHistoryMessage(message) {
    if (!message) {
        return [];
    }

    return message
        .split(/<br\s*\/?>/gi)
        .map((line) => line.trim())
        .filter(Boolean)
        .map(parseChangeLine)
        .filter(Boolean)
        .filter((change) => {
            if (change.typeKey !== "changed component") {
                return true;
            }
            return change.hasStructuredDiff;
        });
}

export function formatHistoryType(type) {
    if (!type) {
        return "—";
    }
    return HISTORY_TYPE_LABELS[type] || type;
}

export function getHistoryVariantClass(variant) {
    switch (variant) {
        case "success":
            return "bg-success";
        case "danger":
            return "bg-danger";
        case "warning":
            return "bg-warning text-dark";
        case "info":
            return "bg-info text-dark";
        case "primary":
            return "bg-primary";
        default:
            return "bg-secondary";
    }
}
